// utils/openingInventoryIdempotency.ts
//
// Client-side identity for the opening-inventory bulk import (INV-OPEN-1).
//
// A single "submission attempt" (batch id + stable per-row ids) is minted once
// per distinct opening-inventory payload and PERSISTED to company-scoped
// localStorage, so it survives a page reload / component remount (e.g. a lost
// response). It is reused for any subsequent submission of the SAME payload
// (retry / double-click / reload), and cleared only on confirmed success or an
// explicit payload change. A changed payload mints a fresh attempt (a new
// operation identity the backend routes to its reverse-and-repost correction).

export type BatchIdRef = { current: string | null };

/**
 * In-memory "generate once, reuse until explicitly cleared" (kept for callers
 * that don't need reload-persistence; see saleIdempotencyKey.ts).
 */
export function getOrCreateBatchId(
  ref: BatchIdRef,
  generate: () => string,
): string {
  if (!ref.current) {
    ref.current = generate();
  }
  return ref.current;
}

export function randomBatchId(): string {
  return crypto.randomUUID();
}

/** Stable per-row client id within one batch (persisted with the attempt). */
export function clientRowId(index: number): string {
  return `row-${index}`;
}

// --------------------------------------------------------------------------- //
// Reload-persistent attempt (localStorage, company-scoped)
// --------------------------------------------------------------------------- //

export type OpeningInventoryRow = {
  product_name?: string | null;
  sku?: string | null;
  qty?: string | number | null;
  unit_cost?: string | number | null;
  selling_price?: string | number | null;
  memo?: string | null;
};

export type PersistedAttempt = {
  batchId: string;
  rowIds: string[];
  canonical: string;
};

function storageKey(companyKey: string): string {
  return `nimos:openingInventoryAttempt:${companyKey || "default"}`;
}

/**
 * Canonical decimal string for a money/quantity value, using PURE STRING
 * operations only — never JavaScript's numeric type, which would lose precision
 * on large integers / long fractions and can emit scientific notation.
 *
 * Rules (identical to the backend `_canon_amount`,
 * `format(Decimal(str(x)).normalize(), "f")`): trim; normalize a leading +/-;
 * drop unnecessary leading integer zeros and trailing fractional zeros; drop
 * the point when no fractional digits remain; map any negative zero to "0";
 * preserve the exact remaining digits; no exponent.
 *
 * Input contract: a PLAIN DECIMAL LITERAL — optional sign, integer digits,
 * optional single ".", fractional digits; no exponent. This is the form the
 * wizard produces for these columns. Exponent-form inputs (e.g. "1e3") are
 * outside the contract and NOT guaranteed to match the backend.
 */
export function canonAmount(value: unknown): string {
  let s = String(value ?? "").trim();
  if (s === "") return "0";

  let negative = false;
  if (s[0] === "+" || s[0] === "-") {
    negative = s[0] === "-";
    s = s.slice(1);
  }

  const dot = s.indexOf(".");
  let intPart = dot === -1 ? s : s.slice(0, dot);
  let fracPart = dot === -1 ? "" : s.slice(dot + 1);

  intPart = intPart.replace(/^0+/, "");
  fracPart = fracPart.replace(/0+$/, "");
  if (intPart === "") intPart = "0";

  const magnitude = fracPart ? `${intPart}.${fracPart}` : intPart;
  if (magnitude === "0") return "0"; // includes negative zero
  return (negative ? "-" : "") + magnitude;
}

/**
 * Deterministic canonical string of the material submission payload. Two
 * submissions are "the same attempt" iff this matches. Field normalization
 * mirrors the backend idempotency fingerprint (`_inventory_row_fingerprint`):
 * name/SKU trimmed+lowercased, amounts via canonAmount, memo trimmed.
 *
 * Guarantee (verified by shared vector tests): for any material value that is a
 * plain decimal literal / trimmed string — the form the wizard produces — this
 * canonical and the backend fingerprint apply identical normalization, so the
 * client never reuses a batch identity the backend would fingerprint
 * differently for such inputs. (A value outside that contract could in
 * principle diverge; the only consequence would be a recoverable spurious 409
 * asking the user to start a new import, never an accounting error.)
 */
export function canonicalizeOpeningInventory(
  rows: OpeningInventoryRow[],
  migrationDate: string,
): string {
  return JSON.stringify({
    date: migrationDate,
    rows: rows.map((r) => ({
      name: String(r.product_name ?? "").trim().toLowerCase(),
      sku: String(r.sku ?? "").trim().toLowerCase() || null,
      qty: canonAmount(r.qty),
      unit_cost: canonAmount(r.unit_cost),
      selling_price: canonAmount(r.selling_price),
      memo: String(r.memo ?? "").trim() || null,
    })),
  });
}

function loadAttempt(companyKey: string): PersistedAttempt | null {
  try {
    const raw = window.localStorage.getItem(storageKey(companyKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.batchId === "string" &&
      Array.isArray(parsed.rowIds) &&
      typeof parsed.canonical === "string"
    ) {
      return parsed as PersistedAttempt;
    }
  } catch {
    // ignore malformed / unavailable storage
  }
  return null;
}

function saveAttempt(companyKey: string, attempt: PersistedAttempt): void {
  try {
    window.localStorage.setItem(storageKey(companyKey), JSON.stringify(attempt));
  } catch {
    // ignore unavailable storage; falls back to in-memory-only behavior
  }
}

/**
 * Resolve the attempt identity for a submission. Returns the persisted attempt
 * if the payload is unchanged (so a reload/retry reuses the same batch and row
 * ids); otherwise mints and persists a fresh attempt.
 */
export function resolveOpeningInventoryAttempt(
  companyKey: string,
  rows: OpeningInventoryRow[],
  migrationDate: string,
  generate: () => string = randomBatchId,
): { batchId: string; rowIds: string[] } {
  const canonical = canonicalizeOpeningInventory(rows, migrationDate);
  const stored = loadAttempt(companyKey);
  if (
    stored &&
    stored.canonical === canonical &&
    stored.rowIds.length === rows.length
  ) {
    return { batchId: stored.batchId, rowIds: stored.rowIds };
  }
  const batchId = generate();
  const rowIds = rows.map((_, i) => clientRowId(i));
  saveAttempt(companyKey, { batchId, rowIds, canonical });
  return { batchId, rowIds };
}

/** Clear the persisted attempt on confirmed success or explicit abandonment. */
export function clearOpeningInventoryAttempt(companyKey: string): void {
  try {
    window.localStorage.removeItem(storageKey(companyKey));
  } catch {
    // ignore
  }
}
